import ProTable, { ActionType } from "@ant-design/pro-table"
import { homeSearch, homeTableAddUpdate, homeTableDel } from "@api/home"
import { acceptImg, productApi } from "@utils/util"
import {Button, Modal, Tabs, message, Space, Drawer, Form, Radio, Switch} from "antd"
import React, {useEffect, useRef, useState} from "react"
import { columnsFn } from "./data"
import ProForm, { 
    ProFormText, 
    ProFormUploadButton, 
    ProFormSelect, 
    ProFormTextArea, 
    ProFormRadio,
    ProFormCascader,
    ProFormDependency,
    ModalForm 
} from '@ant-design/pro-form';
import type { ModalFormProps, ProFormInstance } from '@ant-design/pro-form' 
import {smsSignAdd, smsSignDel, smsSignPageList, userSignGuarantee, userSignGuaranteeStatus} from "@api/product"
import { handleDvmCommonPageListResult } from "@utils/request"
import { useSelector } from "react-redux"
import { StateType } from "@store/index"
import { CERT_COMPANY, CERT_COMPANY_FAIL, CERT_PERSON, CERT_PERSON_FAIL, CERT_UPGRADE_FAIL, calcCertStatus } from "@utils/index"
import UploadButton, { handleUploadFiles } from "@components/Common/Upload"
import {DownloadOutlined, UploadOutlined} from "@ant-design/icons";
import {useExcelUploadCheck} from "@components/MyProduct/Common/useExcelUploadCheck";
import {mobile2elements} from "@api/product/identityCheck";
import {fileExportUrl} from "@api/baseUrl";
import {ExcelUploadPanel} from "@components/MyProduct/Common/ExcelUploadPanel";

const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 18 },
};

// 接口请求的 api 层
const getApi = productApi(smsSignPageList)  // 查询的接口封装
const addUpdateApi = productApi(smsSignAdd) // 列表数据的新增/编辑 接口
const delApi = productApi(smsSignDel) // 表单数据的删除 接口
const getListApi = handleDvmCommonPageListResult(getApi)

const SignNameTooltip = () => {
    return (<div>
        • 建议长度为2-8个字符<br/>
        • 签名内容为短信发送方的真实应用名称、网站名称、公司名称或商标名称<br/>
        • 若涉及到第三方权益必须获得第三方真实授权<br/>
        • 不支持如“客户服务”、“友情提醒”等过于宽泛内容<br/>
        • 通用场景下不支持带“测试”字样的签名，如需测试请选择验证码场景<br/>
        • 若签名／模版内容侵犯到第三方权益必须获得第三方真实授权<br/>
        • 无须添加【】、()、[]符号，签名发送会自带【】符号，避免重复<br/>
        • 禁止发送涉及：色情、赌博、毒品、党政、维权、众筹、慈善募捐、宗教、迷信、股票、移民、面试招聘、 博彩、贷款、催款、信用卡提额、投资理财、中奖、抽奖、一元夺宝、一元秒杀、A货、整形、烟酒、交友、暴力、 恐吓、皮草、返利、代开发票、代理注册、代办证件、加群、加QQ或者加微信、贩卖个人信息、运营商策反、流量 营销等信息的短信
    </div>)
}

const Placeholder = `填写完整可提高通过率，可填写签名来源、应用场景、应用链接等；签名和主体可以查到关联性。填写示例：
        1.  该签名是某某app的名称，用户登录验证；
        2.  该签名是我司微信小程序名称，用于用户消费提醒。
        3.  该签名是我司网站名称，网址是xxxx.com，用户系统注册提醒。`

// 添加签名
interface AddFormProps extends ModalFormProps{
    params?: any;
    onSuccess?: () => void;
}

const AddFormModal = ({onSuccess, params, ...rest}: AddFormProps) => {
    const onFinish = async (values: any) => {
        const { fileList, ...restValues } = values
        const businessLicense = handleUploadFiles(fileList)[0]
        addUpdateApi({...restValues, businessLicense, ...params}, true, onSuccess)
    }
    return (<ModalForm<any>
        layout="horizontal" 
        {...layout} 
        onFinish={onFinish} 
        labelCol={{span: 6}}
        {...rest}>
        <ProFormText 
            name="usName" 
            label="签名名称" 
            rules={[
             {required: true, message: '签名名称不能为空',},
            ]} 
            placeholder="请填写签名名称,建议2～12个字符"
            tooltip={<SignNameTooltip />}
        />
        <ProFormRadio.Group 
           name="usType" 
           label="签名类型" 
           initialValue={1}
           options={[{label: '通用', value: 1}]} 
           rules={[{ required: true, message: '请选择签名类型',}]} 
        />
        <ProFormTextArea 
           name="usApplyDescribe" 
           label="申请说明" 
           fieldProps={{maxLength: 500, rows: 6, showCount: true}} 
           placeholder={Placeholder}
           rules={[
            {required: true, message: '请填写申请说明, 500字以内'}, 
            {max: 500, type:'string', message: '500字以内'}
           ]} 
        />
        
        <ProFormRadio.Group 
           name="relateMe" 
           label="签名来源" 
           initialValue={1}
           options={[{label: '自用（签名为本账号实名认证的网站、App等）', value: 0}, {label: '他用（签名为非本账号实名认证的企业、网站、产品名等）', value: 1},]} 
           rules={[{ required: true, message: '请选择签名来源',}]} 
        />
        <ProFormDependency name={['relateMe']}>
            {
                ({relateMe}) => relateMe ? (<>
                    <ProFormText 
                        name="entName" 
                        label="企业名称" 
                        rules={[
                            {required: true, message: '企业名称不能为空',},
                        ]} 
                        placeholder="请填写企业名称"
                    />
                    <ProFormText 
                        name="entQualificationNum" 
                        label="信用编码" 
                        rules={[
                            {required: true, message: '信用编码不能为空',},
                        ]} 
                        placeholder="请填写社会信用编码"
                    />
                    <ProFormText 
                        name="legalRepresentativeName" 
                        label="法人/经办人姓名" 
                        rules={[
                            {required: true, message: '法人/经办人姓名不能为空',},
                        ]} 
                        placeholder="请填写法人/经办人姓名"
                    />
                    <ProFormText 
                        name="corporateIdCard" 
                        label="法人/经办人身份证号码" 
                        rules={[
                            {required: true, message: '法人/经办人身份证号码不能为空',},
                        ]} 
                        placeholder="请填写法人/经办人身份证号码"
                    />
                    <UploadButton
                        name="fileList"
                        label="营业执照"
                        rules={[{required: true, message: '请上传营业执照'}]}
                        fieldProps={{
                            accept: acceptImg,
                            maxCount: 1
                        }}
                    />
                </>) : null
            }
        </ProFormDependency>
    </ModalForm>)
}

// 签名管理
export default () => {
    const [uploadVisible, setUploadVisible] = useState(false);
    const OnlineRequest = ({ onOk, initialEnabled = false }: { onOk?: () => void; initialEnabled?: boolean }) => {
        const [form] = Form.useForm();
        const [enabled, setEnabled] = useState<boolean>(false);
        const [loading, setLoading] = useState<boolean>(true);

        useEffect(() => {
            const fetchStatus = async () => {
                try {
                    const res = await userSignGuaranteeStatus(); // 👈 查询接口
                    setEnabled(res); // 根据后端返回设置
                } finally {
                    setLoading(false);
                }
            };

            fetchStatus();
        }, []);
        const handleSwitchChange = (value: boolean) => {
            if (value === enabled) return; // 当前状态已选中，不重复弹框
            if(value){
                Modal.confirm({
                    title: '是否确认开启该功能？',
                    content: (
                        <div>
                            <p>开启后，系统会按照兜底验证码签名及模板进行替换发送，具体细则见开通界面说明。</p>
                            <p>我已知晓全部内容，并同意开通。</p>
                        </div>
                    ),
                    onOk: async () => {
                        // 这里应调用实际的开启/关闭接口
                        await userSignGuarantee(value);
                        setEnabled(value);      // 更新本地状态
                        message.success('开启成功!');
                    },
                });
            } else {
                Modal.confirm({
                    title: '是否关闭该功能？',
                    content: (
                        <div>
                            <p>关闭后，系统不会再替换兜底签名发送。</p>
                        </div>
                    ),
                    onOk: async () => {
                        await userSignGuarantee(value);
                        setEnabled(value);      // 更新本地状态
                        message.success('关闭成功!');
                    },

                });
            }
        };
        return (
            <div style={{ padding: '0 20px' }}>
                {/* 标题 + 滑动开关（开关紧贴文字右侧） */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    marginBottom: 12,
                    color: '#333',
                    fontSize: 16
                }}>
                    <span>验证码兜底解决方案</span>
                    <Switch
                        checked={enabled}
                        onChange={handleSwitchChange}
                        loading={loading}
                        checkedChildren="开启"
                        unCheckedChildren="关闭"
                        style={{ marginLeft: 20 }}
                    />
                </div>
                <div style={{
                    marginTop: 24,
                    padding: 16,
                    border: '1px solid #e8e8e8',
                    borderRadius: 6,
                    backgroundColor: '#f9f9f9'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 12, color: '#333', fontSize: 16 }}>
                        <span style={{ marginRight: 8 }}>💡</span>
                        使用说明
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.8, color: '#666' }}>
                        <div style={{ fontWeight: 600, marginTop: 8 }}>应急兜底签名</div>
                        <div style={{ marginBottom: 12 }}>
                            当您通过api发送验证码因签名报备原因发送失败时，系统将自动启用兜底签名，智能提取 code 参数，提升验证码到达率！（仅限 API 调用，群发任务不支持！）兜底签名为系统自动分配，不能自行选择，当前兜底签名可咨询平台客服。
                        </div>

                        <div style={{ fontWeight: 600, marginTop: 16 }}>签名兜底说明</div>
                        <div>正常情况：使用您自行配置的签名与模板发送验证码。</div>
                        <div style={{ marginBottom: 8 }}>异常情况：如您的短信因签名失效状态被拦截、审核失败等，自动切换至灿星云的应急兜底签名，保障业务连续性与用户体验。</div>

                        <div style={{ fontWeight: 600, marginTop: 16 }}>兜底功能限制</div>
                        <div>1. 该功能仅限于API的方式提供给客户的验证码短信发送（群发任务不支持），且只有当客户验证码服务发送失败时系统自动启用。</div>
                        <div>2. 该功能仅限于保障如下条件同时满足的情况</div>
                        <div style={{ paddingLeft: 20 }}>a. 开通验证码兜底解决方案的客户；</div>
                        <div style={{ paddingLeft: 20 }}>b. 客户下的签名必须审核通过，且绑定的资质必须审核通过。</div>
                        <div>3. 该功能的发送成功率会受运营商政策变化，而出现一定情况的波动。</div>
                        <div style={{ marginBottom: 8 }}>4. 本功能目前处于免费体验阶段，旨在为您提供更优质的服务体验。后续将根据使用反馈持续优化，敬请关注！</div>

                        <div style={{ fontWeight: 600, marginTop: 16 }}>潜在影响</div>
                        <div>1. 最终用户收到的短信签名内容不再为客户原始发送的签名。</div>
                        <div>2. 实际下发时，系统会将下发模板内容替换成固定内容：尊敬的用户，您的验证码为：code，请勿泄露于他人！且其中code字段为客户传入的code值内容。</div>
                        <div>3. 若客户下发的验证码模板中无code字段，则该功能不生效。</div>
                        <div>4. 控制台发送量统计时无法根据实际发送签名进行统计，但可以通过实际发送签名进行发送记录查询、统计。</div>
                        <div style={{ marginBottom: 8 }}>5. 潜在可能跟其他客户的验证码在同一个对话框里。</div>

                        <div style={{ fontWeight: 600, marginTop: 16 }}>合规性要求</div>
                        <div>1. 请注意，验证码兜底解决方案作为您自愿开通的服务，仅作为保障您在通过灿星云初审后等待运营商最终审核期间能及时、安全地使用验证码服务的补强策略，在您自定义的短信签名和短信内容通过运营商审核并上线后，系统将自动恢复为您自定义的短信签名和短信模板。</div>
                        <div style={{ marginBottom: 8 }}>2. 如您未按运营商的要求申请和使用签名，或您的短信中存在违法违规内容，短信将会被运营商拦截致发送失败。您对于发送短信的行为和产生的后果自行承担责任，因您的短信内容违法违规导致我司或运营商损失的（如短信端口被关停或被监管通报、约谈、罚款、行政处罚等）您应予以赔偿，我司有权暂停或终止短信服务。</div>
                    </div>
                </div>
            </div>
        );
    };

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [modalProps, setModalProps] = useState({visible: false, title: '新增', params: {}})
    const { baseInfo, certInfo } = useSelector((store: StateType) => store?.userInfo) as any
    const { userNature: type, verifyState: status } = (baseInfo || {}) as any
    const STATUS = calcCertStatus(type, status)
    const ref = useRef<ActionType>(null); // ProTable 组件的 ref
    // operate 函数为增删改查需要处理的业务
    const operate = (type: any, record: any) => {
        const {usId, usName, usType, usCode, createTime, utlVerifyState } = record || {}
        if (type === '新增') {
            // 需求方说 认证失败{个人/企业} 不允许新增
            if ([CERT_PERSON_FAIL, CERT_COMPANY_FAIL].includes(STATUS)) return message.warning('实名认证失败，不允许操作')
            setModalProps({
                ...modalProps,
                title: '新增',
                params: {},
                visible: true,
            })
        } else if (type === '修改') {

            Modal.info({
                title: '是否确认开启该功能？',
                content: (
                    <div>
                        <p>开启后，系统会按照兜底验证码签名及模板进行替换发送，具体细则见开通界面说明。</p>
                    </div>
                ),
                onOk() {},
            })

        }  else if (type === '详情') {
            Modal.info({
                title: '详情信息',
                content: (
                  <div>
                    <p>签名名称：{usName}</p>
                    <p>适用场景：{usType}</p>
                    <p>签名CODE： {usCode}</p>
                    <p>创建时间：{createTime}</p>
                    <p>审核状态：{utlVerifyState}</p>
                  </div>
                ),
                onOk() {},
            })
        } else if (type === '删除') {
            const usIds = record ? [usId] : selectedRowKeys
            Modal.confirm({
                title: '确认要删除该数据吗?',
                content: '删除后当前内容将永久删除，不可恢复。',
                okText: '确认',
                centered: true,
                cancelText: '取消',
                onOk: async() => {
                    await delApi({usIds: usIds.toString()}, true, () => {
                        reFresh()
                    })
                },
            });
        }
    }

    const columns = columnsFn(operate) // 表格列配置

    // 新增/修改提交表单后的回调操作
    const onSuccess = () => {
        console.log('处理后的成功回调触发了')
        setModalProps({
            ...modalProps,
            visible: false
        })
        reFresh()
    }
    // 刷新页面
    const reFresh = () => {
        // @ts-ignore
        ref.current?.clearSelected()
        ref.current?.reload()
    }
    const calcDelDisabled = () => selectedRowKeys.length
    
    return (<><ProTable<any>
        style={{flex: 1, overflow: 'scroll'}}
        scroll={{x: 1300}}
        columns={columns}
        params={{}} // 查询所需要的额外参数
        request={getListApi}
        options={{reload: false, density: false, setting: false}}
        toolbar={{
            actions: [
                <Button key="btn0" type='primary' onClick={() => operate('新增', null)}>添加签名</Button>,
                <Button
                    key="online"
                    type="primary"
                    onClick={() => setUploadVisible(true)}
                    style={{ marginLeft: 8 }}
                >
                    <Space>
                        <span>兜底签名</span>
                    </Space>
                </Button>,
                <Button key="btn1" type='primary' disabled={!calcDelDisabled()} onClick={() => operate('删除', null)}>删除</Button>,
            ],
        }}
        rowKey="usId"
        pagination={{defaultPageSize: 10, showSizeChanger: true, showQuickJumper: true}}
        actionRef={ref as any}
        search={{
            optionRender: (searchConfig, formProps, dom) => [ ...dom.reverse() ]
        }}
        rowSelection={{
            preserveSelectedRowKeys: true,
            onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => setSelectedRowKeys(selectedRowKeys),
        }}
    />
    <AddFormModal
        modalProps={{
            maskClosable: false, 
            destroyOnClose: true,
            onCancel: () => setModalProps({...modalProps, visible: false})
        }}
        onSuccess={onSuccess} 
        {...modalProps} 
    />
    <Drawer
        title={
            <Space>
                <span style={{ fontSize: 16, fontWeight: 'bold' }}>兜底签名</span>
            </Space>
        }
        open={uploadVisible}
        width={800}
        onClose={() => {
            setUploadVisible(false);
        }}
        destroyOnClose
    >
        <OnlineRequest
            onOk={() => {
                setUploadVisible(false);
                setTimeout(() => {
                    reFresh();
                }, 1000);
            }}
        />
    </Drawer>
    </>)
}
